import React from 'react';
import { BaseQuestion, shouldShowAnswerOptions } from './types';
import { CardOption } from './CardOption';
import { QuestionFooter } from './QuestionHelpers';

interface RadioQuestionProps {
  question: BaseQuestion;
  value: string;
  onChange: (value: string) => void;
  onBlur: () => void;
  error?: string;
  primaryColor: string;
  moreInfo?: string;
  isMoreInfoExpanded?: boolean;
  onToggleMoreInfo?: () => void;
}

export const RadioQuestion: React.FC<RadioQuestionProps> = ({
  question,
  value,
  onChange,
  onBlur,
  error,
  primaryColor,
  moreInfo,
  isMoreInfoExpanded,
  onToggleMoreInfo,
}) => {
  if (!shouldShowAnswerOptions(question) || !question.answers) {
    return null;
  }

  const filteredAnswers = question.answers.filter(answer => !(answer as any)._destroy);

  // Check if we should use cards display
  const useCards = question.options?.displayType === 'cards' || filteredAnswers.length <= 10;

  if (useCards) {
    return (
      <div>
        <div className={`grid grid-cols-1 gap-3 ${error ? 'p-3 border-2 border-red-500 rounded-lg' : ''}`}>
          {filteredAnswers.map(answer => (
            <CardOption
              key={answer.id}
              option={answer.answer}
              optionId={answer.id}
              selected={value === String(answer.id)}
              onClick={() => {
                // Allow deselection if question is not required
                if (!question.required && value === String(answer.id)) {
                  onChange('');
                } else {
                  onChange(String(answer.id));
                }
                onBlur();
              }}
              type="radio"
              primaryColor={primaryColor}
            />
          ))}
        </div>
        <QuestionFooter
          error={error}
          helpText={question.description}
          moreInfo={moreInfo}
          isMoreInfoExpanded={isMoreInfoExpanded}
          onToggleMoreInfo={onToggleMoreInfo}
        />
      </div>
    );
  }

  // Regular radio button list
  return (
    <div>
      <div className={`space-y-2 ${error ? 'p-3 border-2 border-red-500 rounded-lg' : ''}`}>
        {filteredAnswers.map(answer => (
          <div
            key={answer.id}
            className="flex items-center cursor-pointer"
            onClick={() => {
              // Allow deselection if question is not required
              if (!question.required && value === String(answer.id)) {
                onChange('');
              } else if (value !== String(answer.id)) {
                onChange(String(answer.id));
              }
              onBlur();
            }}
          >
            <input
              type="radio"
              id={`${question.id}-${answer.id}`}
              name={question.id}
              value={answer.id}
              checked={value === String(answer.id)}
              onChange={() => {}} // Handled by parent div click
              className="h-4 w-4 border-gray-300 pointer-events-none"
              style={{ accentColor: primaryColor }}
            />
            <label
              htmlFor={`${question.id}-${answer.id}`}
              className="ml-2 text-sm text-gray-700 cursor-pointer flex-1"
            >
              {answer.answer}
            </label>
          </div>
        ))}
      </div>
      <QuestionFooter
        error={error}
        helpText={question.description}
        moreInfo={moreInfo}
        isMoreInfoExpanded={isMoreInfoExpanded}
        onToggleMoreInfo={onToggleMoreInfo}
      />
    </div>
  );
};